import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { onIdTokenChanged, signOut, getIdTokenResult } from "firebase/auth";
import type { User } from "firebase/auth";

import { auth, db } from "@/firebase";
import type { UserDoc, FirmSettingsDoc, FilersHubStaffRole, AppUserDoc } from "../types";
import { isStaffRole } from "../types";
import { getFirmSettings } from "../services/firestoreService";
import { useTenant } from "./TenantContext";

interface AuthContextType {
  user: User | null;
  userDoc: UserDoc | null;
  appUser: AppUserDoc | null;
  firmId: string | null;
  staffFirmId: string | null;
  staffRole: FilersHubStaffRole | null;
  firmSettings: FirmSettingsDoc;
  loading: boolean;
  isClient: boolean;
  isStaff: boolean;
  isFirmOwner: boolean;
  logout: () => Promise<void>;
  refreshFirmSettings: () => Promise<void>;
}

type AppBrandingDoc = {
  defaultLogoUrl?: string;
  defaultBrandColor?: string;
  defaultFirmName?: string;
};

// Safe session helpers (iframe-safe)
const safeSessionGet = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionSet = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // iframe may block storage
  }
};

const safeSessionClear = () => {
  try {
    sessionStorage.clear();
  } catch {
    // ignore
  }
};

const DEFAULT_BRAND_COLOR_FALLBACK = "#4aa936";
const DEFAULT_FIRM_NAME_FALLBACK = "Filers Hub";
const DEFAULT_LOGO_URL_FALLBACK =
  "https://storage.googleapis.com/msgsndr/4X2JY0JipOsTk1oyWC4a/media/696fc909eb392ba0f605d913.png";

const DEFAULT_SETTINGS: FirmSettingsDoc = {
  name: DEFAULT_FIRM_NAME_FALLBACK,
  logoUrl: DEFAULT_LOGO_URL_FALLBACK,
  brandColor: DEFAULT_BRAND_COLOR_FALLBACK,
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  userDoc: null,
  appUser: null,
  firmId: null,
  staffFirmId: null,
  staffRole: null,
  firmSettings: DEFAULT_SETTINGS,
  loading: true,
  isClient: false,
  isStaff: false,
  isFirmOwner: false,
  logout: async () => {},
  refreshFirmSettings: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const DEMO_FIRM_ID = "hannah-financial-demo";
const DEMO_LOGO =
  "https://storage.googleapis.com/msgsndr/ygqUk4JVwxGzD160zhvF/media/6969496b197f717a56ac3042.png";
const DEMO_BRAND_COLOR = "#a40000";
const DEMO_FIRM_NAME = "Hannah Financial";

const getFirmIdFromPath = (): string | null => {
  const seg = window.location.pathname.split("/").filter(Boolean);
  if (seg[0] === "location" && seg[1]) return seg[1];
  return null;
};

const normalizeRole = (r: any): string => (r || "").toString().trim().toLowerCase();

const isOwnerish = (role: any) => {
  const r = normalizeRole(role);
  return r === "firmowner" || r === "owner" || r === "admin";
};

// resolve role from multiple possible fields
const resolveRoleFromDoc = (docLike: any): any => {
  if (!docLike) return null;
  return docLike.role || docLike.filershubRole || docLike.ghlRole || null;
};

// ensure userDoc.role is populated even if stored as filershubRole
const normalizeUserDoc = (raw: any): UserDoc => {
  if (!raw) return raw as any;
  const resolved = resolveRoleFromDoc(raw);
  if (!raw.role && resolved) {
    return { ...raw, role: resolved } as UserDoc;
  }
  return raw as UserDoc;
};

const buildFirmSettings = (
  firmDoc: Partial<FirmSettingsDoc> | null | undefined,
  branding: AppBrandingDoc | null | undefined
): FirmSettingsDoc => {
  const name =
    (firmDoc?.name || "").toString().trim() ||
    (branding?.defaultFirmName || "").toString().trim() ||
    DEFAULT_FIRM_NAME_FALLBACK;

  const brandColor =
    (firmDoc?.brandColor || "").toString().trim() ||
    (branding?.defaultBrandColor || "").toString().trim() ||
    DEFAULT_BRAND_COLOR_FALLBACK;

  const logoUrl =
    (firmDoc?.logoUrl || "").toString().trim() ||
    (branding?.defaultLogoUrl || "").toString().trim() ||
    DEFAULT_LOGO_URL_FALLBACK;

  return { name, brandColor, logoUrl } as FirmSettingsDoc;
};

const parseDemoSession = (): { role?: string; isClient?: boolean } | null => {
  const raw = safeSessionGet("FILERSHUB_DEMO_SESSION");
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
    return null;
  } catch {
    return null;
  }
};

// ✅ Demo identity builder (role-aware)
const buildDemoUserDoc = (roleRaw: string | null): UserDoc => {
  const raw = (roleRaw || "CLIENT").toString().trim();

  // Normalize to the same strings your app uses elsewhere
  const lower = raw.toLowerCase();
  const normalized =
    lower === "firmowner" || lower === "firm owner" || lower === "owner" || lower === "admin"
      ? "FirmOwner"
      : lower === "manager"
      ? "Manager"
      : lower === "taxpro" || lower === "tax pro" || lower === "preparer"
      ? "Tax Pro"
      : "CLIENT";

  const uid = safeSessionGet("FILERSHUB_DEMO_UID") || `demo_${normalized.replace(/\s+/g, "_")}`;

  return {
    uid,
    firmId: DEMO_FIRM_ID,
    locationId: DEMO_FIRM_ID as any,
    email: `${normalized.toLowerCase().replace(/\s+/g, "")}@hannahfinancial.com`,
    displayName: `Hannah ${normalized}`,
    role: normalized as any,
    filershubRole: normalized as any,
    createdAt: new Date().toISOString() as any,
  } as any;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { firm: tenantFirm } = useTenant();

  const [user, setUser] = useState<User | null>(null);
  const [userDoc, setUserDoc] = useState<UserDoc | null>(null);
  const [appUser, setAppUser] = useState<AppUserDoc | null>(null);
  const [firmId, setFirmId] = useState<string | null>(null);
  const [staffRole, setStaffRole] = useState<FilersHubStaffRole | null>(null);
  const [loading, setLoading] = useState(true);

  const [branding, setBranding] = useState<AppBrandingDoc | null>(null);
  const [firmDocSettings, setFirmDocSettings] = useState<Partial<FirmSettingsDoc> | null>(null);

  const firmSettings = useMemo(() => {
    const firmFromTenant = tenantFirm ? (tenantFirm as any) : null;
    const mergedFirm = firmFromTenant || firmDocSettings || null;
    return buildFirmSettings(mergedFirm, branding);
  }, [tenantFirm, firmDocSettings, branding]);

  useEffect(() => {
    const unsubBranding = onSnapshot(
      doc(db, "appConfig", "branding"),
      (snap) => {
        if (snap.exists()) setBranding(snap.data() as AppBrandingDoc);
        else setBranding(null);
      },
      () => setBranding(null)
    );

    return () => unsubBranding();
  }, []);

  useEffect(() => {
    const tfId = (tenantFirm as any)?.id || null;
    if (tfId) setFirmId(tfId);
  }, [tenantFirm]);

  useEffect(() => {
    // ✅ DEMO MODE: bypass Firebase auth and create a role-aware userDoc
    const demoSession = parseDemoSession();
    const pathFirmId = getFirmIdFromPath();
    const isDemoFirm = pathFirmId === DEMO_FIRM_ID;

    if (demoSession && isDemoFirm) {
      const demoRole = demoSession.role || "CLIENT";
      const demoUserDoc = buildDemoUserDoc(demoRole);

      setUser(null);
      setAppUser(null);
      setUserDoc(demoUserDoc);
      setFirmId(DEMO_FIRM_ID);
      safeSessionSet("FILERSHUB_SSO_LOC", DEMO_FIRM_ID);

      // staffRole drives staff UI, set it for staff roles
      const r = normalizeRole(demoUserDoc.role);
      if (r === "firmowner") setStaffRole("FirmOwner" as any);
      else if (r === "manager") setStaffRole("Manager" as any);
      else if (r === "tax pro" || r === "taxpro") setStaffRole("Tax Pro" as any);
      else setStaffRole(null);

      // Firm branding for demo
      setFirmDocSettings({ name: DEMO_FIRM_NAME, logoUrl: DEMO_LOGO, brandColor: DEMO_BRAND_COLOR } as any);

      setLoading(false);
      return;
    }

    let unsubFirm: (() => void) | null = null;
    let unsubFirmUser: (() => void) | null = null;
    let unsubUsers: (() => void) | null = null;

    const cleanupSnapshots = () => {
      try {
        unsubFirm?.();
        unsubFirmUser?.();
        unsubUsers?.();
      } catch {}
      unsubFirm = null;
      unsubFirmUser = null;
      unsubUsers = null;
    };

    const unsubscribeAuth = onIdTokenChanged(auth, async (authUser) => {
      cleanupSnapshots();

      if (!authUser) {
        setUser(null);
        setUserDoc(null);
        setAppUser(null);
        if (!tenantFirm) setFirmId(null);
        setStaffRole(null);
        setFirmDocSettings(null);
        setLoading(false);
        return;
      }

      setUser(authUser);
      setLoading(true);

      try {
        const tokenResult = await getIdTokenResult(authUser, true);

        const claimLocationId = tokenResult.claims.locationId as string | undefined;
        const claimFirmId = tokenResult.claims.firmId as string | undefined;

        const resolvedFirmId =
          claimLocationId ||
          claimFirmId ||
          getFirmIdFromPath() ||
          (tenantFirm as any)?.id ||
          safeSessionGet("FILERSHUB_SSO_LOC") ||
          null;

        const roleFromClaim =
          (tokenResult.claims.role as FilersHubStaffRole | undefined) ||
          ((tokenResult.claims.filershubRole as any) as FilersHubStaffRole | undefined) ||
          ((tokenResult.claims.staffRole as any) as FilersHubStaffRole | undefined);

        setFirmId(resolvedFirmId || null);
        setStaffRole(roleFromClaim || null);

        if (resolvedFirmId) safeSessionSet("FILERSHUB_SSO_LOC", resolvedFirmId);

        const fetchProfile = async () => {
          const globalRef = doc(db, "users", authUser.uid);
          const globalSnap = await getDoc(globalRef);
          if (globalSnap.exists()) {
            setUserDoc(normalizeUserDoc(globalSnap.data() as any));
            return;
          }

          const appUserRef = doc(db, "appUsers", authUser.uid);
          const appUserSnap = await getDoc(appUserRef);
          if (appUserSnap.exists()) {
            const au = appUserSnap.data() as AppUserDoc;
            setAppUser(au);
            setUserDoc(normalizeUserDoc(au as any));
            return;
          }

          if (resolvedFirmId) {
            const firmUserRef = doc(db, "firms", resolvedFirmId, "users", authUser.uid);
            const firmUserSnap = await getDoc(firmUserRef);
            if (firmUserSnap.exists()) setUserDoc(normalizeUserDoc(firmUserSnap.data() as any));
          }
        };

        await fetchProfile();

        if (resolvedFirmId) {
          unsubFirm = onSnapshot(
            doc(db, "firms", resolvedFirmId),
            (snap) => {
              if (snap.exists()) setFirmDocSettings(snap.data() as FirmSettingsDoc);
              else setFirmDocSettings(null);
            },
            () => setFirmDocSettings(null)
          );

          unsubFirmUser = onSnapshot(
            doc(db, "firms", resolvedFirmId, "users", authUser.uid),
            (snap) => {
              if (snap.exists()) setUserDoc(normalizeUserDoc(snap.data() as any));
            }
          );
        } else {
          setFirmDocSettings(null);
        }

        unsubUsers = onSnapshot(doc(db, "users", authUser.uid), (snap) => {
          if (!resolvedFirmId && snap.exists()) {
            setUserDoc(normalizeUserDoc(snap.data() as any));
          }
        });
      } catch (err) {
        console.error("[AuthContext] identity resolution failed:", err);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      cleanupSnapshots();
      unsubscribeAuth();
    };
  }, [tenantFirm]);

  const logout = async () => {
    setUser(null);
    setUserDoc(null);
    setAppUser(null);
    setStaffRole(null);
    setFirmDocSettings(null);

    safeSessionClear();

    try {
      await signOut(auth);
    } catch {}

    window.location.href = window.location.origin;
  };

  const refreshFirmSettings = async () => {
    if (!firmId) return;
    try {
      const s = await getFirmSettings(firmId);
      setFirmDocSettings(s);
    } catch {}
  };

  const isStaff = useMemo(() => {
    const docRole = resolveRoleFromDoc(userDoc as any);
    const appRole = resolveRoleFromDoc(appUser as any);
    return isStaffRole(staffRole) || isStaffRole(docRole) || isStaffRole(appRole);
  }, [staffRole, userDoc, appUser]);

  const isClient = useMemo(() => !isStaff, [isStaff]);

  const isFirmOwner = useMemo(() => {
    const docRole = resolveRoleFromDoc(userDoc as any);
    const appRole = resolveRoleFromDoc(appUser as any);
    return isOwnerish(staffRole) || isOwnerish(docRole) || isOwnerish(appRole);
  }, [staffRole, userDoc, appUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        userDoc,
        appUser,
        firmId,
        staffFirmId: firmId,
        staffRole,
        firmSettings,
        loading,
        isClient,
        isStaff,
        isFirmOwner,
        logout,
        refreshFirmSettings,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
